import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BranchDetail, BranchOperationStatus, ReviewStatus } from "../../../types/admin";
import { formatDate, reviewStatusLabelMap } from "../../../utils/admin";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { deleteBranch, fetchBranchDetail, updateBranch } from "../../../store/slices/branchesSlice";
import { invalidateMembersList } from "../../../store/slices/membersSlice";

interface BranchInfoTabProps {
  detail: BranchDetail;
}

const BranchInfoTab: React.FC<BranchInfoTabProps> = ({ detail }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { updating, deleting } = useAppSelector((state) => state.branches);

  const [isEditing, setIsEditing] = useState(false);
  const [editStatus, setEditStatus] = useState<BranchOperationStatus>("운영");
  const [editReviewStatus, setEditReviewStatus] = useState<ReviewStatus>("pending");

  useEffect(() => {
    if (!isEditing) {
      setEditStatus(detail.status);
      setEditReviewStatus(detail.review_status);
    }
  }, [detail, isEditing]);

  const onStatusChange = (nextStatus: BranchOperationStatus) => {
    setEditStatus(nextStatus);
    if (nextStatus === "운영") {
      setEditReviewStatus("approved");
    } else if (nextStatus === "심사대기") {
      setEditReviewStatus("pending");
    } else if (nextStatus === "반려") {
      setEditReviewStatus("rejected");
    }
  };

  const handleUpdate = async () => {
    try {
      await dispatch(
        updateBranch({
          branchId: detail.branch_id,
          status: editStatus !== detail.status ? editStatus : undefined,
          review_status: editReviewStatus !== detail.review_status ? editReviewStatus : undefined,
        })
      ).unwrap();
      dispatch(invalidateMembersList());
      await dispatch(fetchBranchDetail(detail.branch_id));
      window.alert(
        "지점 정보가 저장되었습니다.\n운영 상태 변경 시 해당 사업주 회원 상태(허용/반려/심사 대기)도 함께 동기화됩니다."
      );
      setIsEditing(false);
    } catch (error) {
      window.alert((error as string) || "수정에 실패했습니다.");
    }
  };

  const handleDelete = async () => {
    if (window.confirm("정말로 이 지점을 삭제하시겠습니까?\n삭제 시 사업주 회원 상태도 재동기화됩니다.")) {
      try {
        await dispatch(deleteBranch(detail.branch_id)).unwrap();
        dispatch(invalidateMembersList());
        window.alert("지점이 삭제되었습니다.");
        navigate("/admin/branches", { replace: true });
      } catch (error) {
        window.alert((error as string) || "삭제에 실패했습니다.");
      }
    }
  };

  return (
    <>
      <div className="detail-grid wide">
        <article className="detail-card">
          <span>지점명</span>
          <strong>{detail.branch_name}</strong>
        </article>
        <article className="detail-card">
          <span>지점 코드</span>
          <strong>{detail.branch_code}</strong>
        </article>
        <article className="detail-card">
          <span>지역/주소</span>
          <strong>{`${detail.region_summary} / ${detail.address}`}</strong>
        </article>
        <article className="detail-card">
          <span>운영상태</span>
          {isEditing ? (
            <div className="branch-status-edit">
              <select value={editStatus} onChange={(e) => onStatusChange(e.target.value as BranchOperationStatus)}>
                <option value="운영">운영</option>
                <option value="심사대기">심사대기</option>
                <option value="반려">반려</option>
              </select>
              <select
                value={editReviewStatus}
                onChange={(e) => setEditReviewStatus(e.target.value as ReviewStatus)}
              >
                <option value="pending">인증 대기</option>
                <option value="approved">인증 완료</option>
                <option value="rejected">반려</option>
              </select>
              <p className="branch-status-edit__hint">
                운영 → 사업주 「허용」 / 반려 → 「반려」 / 심사대기 → 「심사 대기」 로 동기화됩니다.
              </p>
            </div>
          ) : (
            <strong>{`${detail.status} (${reviewStatusLabelMap[detail.review_status] || detail.review_status})`}</strong>
          )}
        </article>
        <article className="detail-card">
          <span>오픈일</span>
          <strong>{formatDate(detail.opened_at)}</strong>
        </article>
        <article className="detail-card">
          <span>사업자번호</span>
          <strong>{detail.business_number}</strong>
        </article>
        <article className="detail-card">
          <span>점장</span>
          <strong>{detail.manager_name ? `${detail.manager_name} / ${detail.manager_phone_number}` : "없음"}</strong>
        </article>
        <article className="detail-card">
          <span>사업주</span>
          <strong>{detail.owner_name ? `${detail.owner_name} / ${detail.owner_phone_number}` : "없음"}</strong>
        </article>
      </div>

      <div className="detail-actions">
        {isEditing ? (
          <>
            <button className="primary-btn" type="button" onClick={handleUpdate} disabled={updating}>
              {updating ? "저장 중..." : "저장하기"}
            </button>
            <button className="secondary-btn" type="button" onClick={() => setIsEditing(false)} disabled={updating}>
              취소
            </button>
          </>
        ) : (
          <>
            <button className="primary-btn" type="button" onClick={() => setIsEditing(true)}>
              지점정보 수정하기
            </button>
            <button className="secondary-btn danger" type="button" onClick={handleDelete} disabled={deleting}>
              {deleting ? "삭제 중..." : "지점정보 삭제하기"}
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default BranchInfoTab;
